import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './PromoAnalytics.css';

interface FunnelStep {
  label: string;
  count: number;
  conversionFromPrevious: number | null;
}

interface ChannelPerformance {
  channel: string;
  codesGenerated: number;
  codesRedeemed: number;
  redemptionRate: number;
  campaigns: number;
}

interface CampaignMrr {
  campaign: string;
  mrr: number;
  subscriptions: number;
}

interface AnalyticsData {
  totalCodes: number;
  totalRedemptions: number;
  overallConversionRate: number;
  totalMrr: number;
  funnel: {
    codesGenerated: number;
    codesRedeemed: number;
    subscriptionsCreated: number;
    activeSubscriptions: number;
    totalMrr: number;
  };
  channelPerformance: ChannelPerformance[];
  mrrByCampaign: CampaignMrr[];
}

const PromoAnalytics: React.FC = () => {
  const { getAccessToken } = useAuth();

  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Authentication expired. Please log in again.');
        return;
      }

      const response = await fetch(API_ENDPOINTS.promoAnalytics(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `Failed to load analytics (${response.status})`);
      }

      const data = await response.json();
      setAnalytics(data.analytics || data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercent = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const buildFunnelSteps = (): FunnelStep[] => {
    if (!analytics?.funnel) return [];

    const funnel = analytics.funnel;
    const steps: FunnelStep[] = [
      {
        label: 'Codes Generated',
        count: funnel.codesGenerated ?? 0,
        conversionFromPrevious: null,
      },
      {
        label: 'Codes Redeemed',
        count: funnel.codesRedeemed ?? 0,
        conversionFromPrevious:
          funnel.codesGenerated > 0
            ? funnel.codesRedeemed / funnel.codesGenerated
            : 0,
      },
      {
        label: 'Subscriptions Created',
        count: funnel.subscriptionsCreated ?? 0,
        conversionFromPrevious:
          funnel.codesRedeemed > 0
            ? funnel.subscriptionsCreated / funnel.codesRedeemed
            : 0,
      },
      {
        label: 'Active Subscriptions',
        count: funnel.activeSubscriptions ?? 0,
        conversionFromPrevious:
          funnel.subscriptionsCreated > 0
            ? funnel.activeSubscriptions / funnel.subscriptionsCreated
            : 0,
      },
      {
        label: 'Total MRR',
        count: funnel.totalMrr ?? 0,
        conversionFromPrevious: null,
      },
    ];

    return steps;
  };

  const getMaxFunnelCount = (steps: FunnelStep[]): number => {
    return Math.max(...steps.map((s) => s.count), 1);
  };

  if (loading) {
    return (
      <div className="promo-analytics">
        <div className="analytics-loading">
          <div className="analytics-spinner" />
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  const funnelSteps = buildFunnelSteps();
  const maxFunnelCount = getMaxFunnelCount(funnelSteps);

  const sortedChannels = analytics?.channelPerformance
    ? [...analytics.channelPerformance].sort(
        (a, b) => (b.codesRedeemed ?? 0) - (a.codesRedeemed ?? 0)
      )
    : [];

  const sortedMrr = analytics?.mrrByCampaign
    ? [...analytics.mrrByCampaign].sort((a, b) => (b.mrr ?? 0) - (a.mrr ?? 0))
    : [];

  return (
    <div className="promo-analytics">
      <h2 className="promo-analytics-title">Promo Code Analytics</h2>

      {error && (
        <div className="analytics-error-banner">
          <span>{error}</span>
          <button
            className="analytics-error-dismiss"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            X
          </button>
        </div>
      )}

      {!analytics && !error && (
        <p className="analytics-empty">No analytics data available yet.</p>
      )}

      {analytics && (
        <>
          {/* Summary stat cards */}
          <div className="analytics-summary">
            <div className="summary-card">
              <span className="summary-number">{analytics.totalCodes ?? 0}</span>
              <span className="summary-label">Total Codes</span>
            </div>
            <div className="summary-card">
              <span className="summary-number">{analytics.totalRedemptions ?? 0}</span>
              <span className="summary-label">Total Redemptions</span>
            </div>
            <div className="summary-card">
              <span className="summary-number">
                {analytics.overallConversionRate != null
                  ? formatPercent(analytics.overallConversionRate)
                  : '0%'}
              </span>
              <span className="summary-label">Overall Conversion Rate</span>
            </div>
            <div className="summary-card">
              <span className="summary-number summary-mrr">
                {formatCurrency(analytics.totalMrr ?? 0)}
              </span>
              <span className="summary-label">Total MRR</span>
            </div>
          </div>

          {/* Conversion Funnel */}
          {funnelSteps.length > 0 && (
            <div className="funnel-section">
              <h3 className="section-title">Conversion Funnel</h3>
              <div className="funnel-steps">
                {funnelSteps.map((step, idx) => {
                  const isLastStep = idx === funnelSteps.length - 1;
                  const barWidth = isLastStep
                    ? 0
                    : Math.max((step.count / maxFunnelCount) * 100, 2);

                  return (
                    <React.Fragment key={step.label}>
                      <div className="funnel-step">
                        <div className="funnel-step-header">
                          <span className="funnel-step-label">{step.label}</span>
                          <span className={`funnel-step-count${isLastStep ? ' funnel-mrr-value' : ''}`}>
                            {isLastStep ? formatCurrency(step.count) : step.count.toLocaleString()}
                          </span>
                        </div>
                        {!isLastStep && (
                          <div className="funnel-bar-track">
                            <div
                              className="funnel-bar"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        )}
                      </div>
                      {idx < funnelSteps.length - 1 && (
                        <div className="funnel-arrow">
                          <span className="funnel-arrow-icon">|</span>
                          {funnelSteps[idx + 1].conversionFromPrevious != null && (
                            <span className="funnel-conversion-rate">
                              {formatPercent(funnelSteps[idx + 1].conversionFromPrevious!)}
                            </span>
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Channel Performance */}
          {sortedChannels.length > 0 && (
            <div className="channel-section">
              <h3 className="section-title">Channel Performance</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Codes Generated</th>
                    <th>Codes Redeemed</th>
                    <th>Redemption Rate</th>
                    <th>Campaigns</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedChannels.map((ch, idx) => (
                    <tr key={ch.channel || idx}>
                      <td>{ch.channel || '--'}</td>
                      <td>{ch.codesGenerated ?? 0}</td>
                      <td>{ch.codesRedeemed ?? 0}</td>
                      <td>
                        {ch.redemptionRate != null ? formatPercent(ch.redemptionRate) : '0%'}
                      </td>
                      <td>{ch.campaigns ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* MRR by Campaign */}
          {sortedMrr.length > 0 && (
            <div className="mrr-section">
              <h3 className="section-title">MRR by Campaign</h3>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>MRR</th>
                    <th>Subscriptions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedMrr.map((item, idx) => (
                    <tr key={item.campaign || idx}>
                      <td>{item.campaign || '--'}</td>
                      <td className="mrr-cell">{formatCurrency(item.mrr ?? 0)}</td>
                      <td>{item.subscriptions ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PromoAnalytics;
